import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IndexSeriesFile } from '../plate-detail-v2/components/market-info/market-index.model';
import {
  FORMAT_MEDIANS_ASSET_URL,
  FormatMedianRecord,
  FormatMediansAsset,
} from './dateless-market.model';
import { NOTABLE_SALES, NotableSale } from './dateless-sales';
import { Registration } from 'src/app/models/registration';
import { viewerConfigById } from 'src/app/consts/default-image';
import { ImageService } from 'src/app/services/image-service';
import { SearchService } from 'src/app/services/search-service';
import {
  DatelessArrangementFilter,
  DatelessSnapshot,
  DatelessSortKey,
  DatelessStateService,
} from './dateless-state.service';
import {
  arrangementOf,
  excludeRegtransfers,
  layoutForToken,
  matchesArrangement,
} from './register-feed';
import {
  DATELESS_REFERENCE_PATH,
  DatelessFormat,
  FORMAT_GROUPS,
  FormatGroup,
  KNOWN_FORMATS,
  parseFormatToken,
} from 'src/app/components/classification-pill/format-token';
import {
  CHAPTERS,
  Chapter,
  CollectionCard,
  DATELESS_TIMELINE,
  RELATED_COLLECTIONS,
  TIMELINE_SOURCES,
  TimelineMilestone,
  WHERE_NEXT,
  WhereNextCard,
} from './dateless-fixtures';

/* =============================================================================
   PlateX — Dateless · The PlateX Reference.
   Phase 1: page shell + static editorial structure (build pack §12, Phase 1).

   Scope here is deliberately static: hero, "why collectors choose Dateless",
   the historical timeline, the understanding-value prose and the related
   collections. The market band (Phase 3), Historic Issuing Authorities archive
   (Phase 4), search/browse (Phase 5) and the live register (Phase 6) render
   reserved placeholders on this unindexed draft route until their phases land.

   Rules enforced (build pack):
     · copy verbatim from the approved v13 prototype; no invented data
     · no em/en dashes as punctuation anywhere in published copy
     · no illustrative availability, inventory or market figures
     · shell/measure/jump mechanics mirror the register pages
   ========================================================================== */

@Component({
  selector: 'pla-dateless-register',
  templateUrl: './dateless-register.component.html',
  styleUrls: ['./dateless-register.component.scss'],
})
export class DatelessRegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  public chapters: Chapter[] = CHAPTERS;
  public timeline: TimelineMilestone[] = DATELESS_TIMELINE;
  public timelineSources = TIMELINE_SOURCES;
  public collections: CollectionCard[] = RELATED_COLLECTIONS;
  public whereNext: WhereNextCard[] = WHERE_NEXT;

  public reducedMotion = false;

  /* ---- hero render (§6.2) ------------------------------------------------
     The hero is produced by PlateX's own plate viewer, not a third-party
     photograph, so there is no rights question to clear: both the vehicle
     artwork and the plate render are PlateX assets. 25 O is historically the
     mark of a Ferrari 250 GT SWB, which is the vehicle the prototype hero
     shows, so the viewer's SWB configuration is the one used here. Until the
     render resolves (or if the config is ever withdrawn) the reserved frame
     stays on screen; nothing is fabricated in its place. */
  private readonly HERO_VIEWER_CONFIG_ID = 662; // Ferrari 250 GT SWB
  public readonly heroRegistration = '25 O';
  public readonly heroCarName = 'Ferrari 250 GT SWB';
  public heroImage: string | null = null;

  /* ---- market data (Phase 3; static assets, never endpoints) ---- */
  private static indexCache: IndexSeriesFile | null = null;
  private static mediansCache: FormatMediansAsset | null = null;
  public indexFile: IndexSeriesFile | null = null;
  public medians: FormatMediansAsset | null = null;
  public notableSales: NotableSale[] = NOTABLE_SALES;

  /** Derived, never hardcoded: dateless last-full-year value + YoY. */
  public statLastFull: { year: number; value: number; yoyPct: number; multiple: number } | null = null;
  /** Derived: growth since 2015 base for dateless vs composite. */
  public statSinceBase: { dateless: number; composite: number } | null = null;
  /** Value-by-format rows sorted by presentation-window median, descending. */
  public vbfRows: { rec: FormatMedianRecord; barPct: number }[] = [];
  /** The six-row format ladder of the approved table (ascending value). */
  public tableRows: FormatMedianRecord[] = [];

  /* ---- live register (Phase 6; feed-completeness gate passed) ---- */
  public regRows: Registration[] = [];        // complete feed set, Regtransfers excluded
  public regLoading = false;
  public regFailed = false;
  public regLoaded = false;
  public arrangement: DatelessArrangementFilter = 'both';
  public sort: DatelessSortKey = 'price-asc';
  public dealer: string | null = null;
  public dealers: string[] = [];
  public searchTerm = '';
  public page = 1;
  public readonly pageSize = 12;
  private feedSeq = 0;
  private pendingRestore: DatelessSnapshot | null = null;

  /* ---- format browse + deep link (§4, §6.8; Phase 2) ---- */
  public formatGroups: FormatGroup[] = FORMAT_GROUPS;
  /** The format selected via pill or /dateless-draft/:format. Null until chosen. */
  public selectedFormat: DatelessFormat | null = null;
  private routeSub: Subscription | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private searchService: SearchService,
    private imageService: ImageService,
    private state: DatelessStateService
  ) {}

  ngOnInit(): void {
    this.reducedMotion =
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loadMarketData();
    this.buildHeroImage();
    this.injectStructuredData();
    // Back-button restoration (§3.5): transient filters + scroll, never URL.
    this.pendingRestore = this.state.takeFresh();
    if (this.pendingRestore) {
      this.arrangement = this.pendingRestore.arrangement;
      this.sort = this.pendingRestore.sort;
      this.dealer = this.pendingRestore.dealer;
      this.searchTerm = this.pendingRestore.searchTerm;
      this.page = this.pendingRestore.page;
    }
    // ?q= (homepage Dateless tab): characters to search within the format.
    const q = (this.route.snapshot.queryParamMap.get('q') || '').trim();
    if (q) this.searchTerm = q;
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const token = params.get('format');
      if (!token) {
        this.selectedFormat = null;
        return;
      }
      const parsed = parseFormatToken(token);
      const known = parsed && KNOWN_FORMATS[parsed.token];
      if (known) {
        const restoring = !!this.pendingRestore && this.pendingRestore.format === known.token;
        this.selectedFormat = known;
        this.fetchRegister(known.token, restoring);
        if (restoring) {
          const y = this.pendingRestore!.scrollY;
          [200, 600].forEach((t) => setTimeout(() => window.scrollTo(0, y), t));
          this.pendingRestore = null;
        } else {
          // Deep link behaves like a pill click: register scrolled into view.
          // Two-step ladder so the jump lands after the router's own
          // scrollPositionRestoration('top') and initial layout settle.
          [120, 450].forEach((t) => setTimeout(() => this.jumpTo('register'), t));
        }
      } else {
        // unknown token: fall back to no selection on the canonical route (§4.2)
        this.router.navigate([this.basePath], { replaceUrl: true });
      }
    });
  }

  ngAfterViewInit(): void {
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    window.addEventListener('resize', this.measureShellBound, { passive: true });
  }

  /* =============== hero render (§6.2) ==================================== */

  /** Renders 25 O onto the plate viewer's Ferrari 250 GT SWB for the hero. */
  private buildHeroImage(): void {
    const config = viewerConfigById(this.HERO_VIEWER_CONFIG_ID);
    if (!config) return; // reserved frame stays; never a substitute vehicle
    try {
      this.imageService.quickBuild(config, this.heroRegistration, (key) => {
        if (key && key.image) this.heroImage = key.image;
      });
    } catch {
      this.heroImage = null; // WebGL unavailable: reserved frame stays
    }
  }

  /* =============== structured data (§9: WebPage + BreadcrumbList) ======== */
  private ldScript: HTMLScriptElement | null = null;

  private injectStructuredData(): void {
    if (typeof document === 'undefined' || this.ldScript) return;
    const origin = 'https://www.platex.co.uk';
    const payload = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Dateless Number Plates - The PlateX Reference',
          description: 'Britain\'s original registrations, issued 1903 to 1963. The history, the market, format values and the live register of dateless plates.',
          url: origin + '/plates/dateless',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: origin + '/' },
            { '@type': 'ListItem', position: 2, name: 'Plates', item: origin + '/plates' },
            { '@type': 'ListItem', position: 3, name: 'Dateless', item: origin + '/plates/dateless' },
          ],
        },
      ],
    };
    this.ldScript = document.createElement('script');
    this.ldScript.type = 'application/ld+json';
    this.ldScript.text = JSON.stringify(payload);
    document.head.appendChild(this.ldScript);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.measureShellBound);
    if (this.routeSub) this.routeSub.unsubscribe();
    if (this.ldScript && this.ldScript.parentNode) {
      this.ldScript.parentNode.removeChild(this.ldScript);
      this.ldScript = null;
    }
    if (this.selectedFormat) {
      this.state.save({
        format: this.selectedFormat.token,
        arrangement: this.arrangement,
        sort: this.sort,
        dealer: this.dealer,
        searchTerm: this.searchTerm,
        page: this.page,
        resultAnchorId: null,
        scrollY: typeof window !== 'undefined' ? Math.round(window.scrollY) : 0,
      });
    }
  }

  /* =============== live register (Phase 6) =============== */
  private fetchRegister(token: string, keepFilters = false): void {
    const layout = layoutForToken(token);
    if (!layout) return;
    if (!keepFilters) {
      this.arrangement = 'both';
      this.dealer = null;
      this.page = 1;
    }
    const seq = ++this.feedSeq;
    this.regLoading = true;
    this.regFailed = false;
    this.searchService.searchDatelessLengthCanonical(layout, this.searchTerm.trim().toUpperCase(), (res) => {
      if (seq !== this.feedSeq) return; // stale response guard
      this.regLoading = false;
      if (!res || !(res as any).main) {
        this.regFailed = true;
        this.regRows = [];
        this.regLoaded = true;
        return;
      }
      // HARD RULE: Regtransfers never reaches a public surface (§5, test #14).
      this.regRows = excludeRegtransfers((res as any).main as Registration[]);
      this.dealers = Array.from(new Set(this.regRows.map((r: any) => r.seller).filter(Boolean))).sort();
      this.regLoaded = true;
    });
  }

  /** Filtered + sorted view over the COMPLETE feed set (gate-verified). */
  public get regView(): Registration[] {
    let rows = this.regRows.filter((r) => matchesArrangement(r, this.arrangement));
    if (this.dealer) rows = rows.filter((r: any) => r.seller === this.dealer);
    rows = [...rows];
    if (this.sort === 'price-asc') rows.sort((a: any, b: any) => (a.price || 0) - (b.price || 0));
    else if (this.sort === 'price-desc') rows.sort((a: any, b: any) => (b.price || 0) - (a.price || 0));
    else rows.sort((a: any, b: any) => (a.registration || '').localeCompare(b.registration || ''));
    return rows;
  }

  public get regPage(): Registration[] {
    const start = (this.page - 1) * this.pageSize;
    return this.regView.slice(start, start + this.pageSize);
  }

  public get pageCount(): number {
    return Math.max(1, Math.ceil(this.regView.length / this.pageSize));
  }

  public get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, i) => i + 1);
  }

  public setPage(p: number): void {
    this.page = Math.max(1, Math.min(this.pageCount, p));
    this.jumpTo('register');
  }

  public setArrangement(a: DatelessArrangementFilter): void {
    if (this.arrangement === a) return;
    this.arrangement = a;
    this.page = 1;
  }

  public setSort(s: string): void {
    this.sort = s as DatelessSortKey;
    this.page = 1;
  }

  public setDealer(d: string): void {
    this.dealer = d === 'all' ? null : d;
    this.page = 1;
  }

  public runSearch(): void {
    // Search writes the register for the selected (or default) format; both
    // arrangements are searched together (§6.8). Not a route change.
    const target = this.selectedFormat || KNOWN_FORMATS['1x2'];
    if (!this.selectedFormat) {
      this.router.navigate([this.basePath, target.token]);
      return;
    }
    this.page = 1;
    this.fetchRegister(target.token, true);
  }

  public get selectedMedianRecord(): FormatMedianRecord | null {
    if (!this.medians || !this.selectedFormat) return null;
    return this.medians.records.find(
      (r) => r.classification.token === this.selectedFormat!.token
    ) || null;
  }

  public trackByRow(_i: number, row: Registration): string {
    return (row as any).id || row.registration;
  }

  /* ---- register row presentation ---- */

  /** Spaced plate text as the feed formats it (e.g. "8 UW"). */
  public plateText(row: Registration): string {
    return (row as any).formattedRegistration || row.registration || '';
  }

  public isPOA(row: Registration): boolean {
    const p = (row as any).price;
    return !p || p >= 1000000000; // POA sentinel; feed prices are in pence
  }

  public price(row: Registration): string {
    if (this.isPOA(row)) return 'POA';
    return '£' + Math.round((row as any).price / 100).toLocaleString('en-GB');
  }

  /** Visible-order label for the row, derived from the plate itself. */
  public arrangementLabel(row: Registration): string {
    const a = arrangementOf(row);
    if (a === 'letter-first') return 'Letter first';
    if (a === 'number-first') return 'Number first';
    return '';
  }

  public plateLink(reg: string): string[] {
    return ['/plate', (reg || '').replace(/\s+/g, '')];
  }

  /* =============== market data (Phase 3) =============== */
  private loadMarketData(): void {
    if (DatelessRegisterComponent.indexCache) {
      this.ingestIndex(DatelessRegisterComponent.indexCache);
    } else {
      this.http.get<IndexSeriesFile>('assets/market-index/platex-index-series.json')
        .subscribe((file) => {
          DatelessRegisterComponent.indexCache = file;
          this.ingestIndex(file);
        }, () => { /* chart and stats simply do not render */ });
    }
    if (DatelessRegisterComponent.mediansCache) {
      this.ingestMedians(DatelessRegisterComponent.mediansCache);
    } else {
      this.http.get<FormatMediansAsset>(FORMAT_MEDIANS_ASSET_URL)
        .subscribe((asset) => {
          DatelessRegisterComponent.mediansCache = asset;
          this.ingestMedians(asset);
        }, () => { /* table and value panel simply do not render */ });
    }
  }

  private ingestIndex(file: IndexSeriesFile): void {
    this.indexFile = file;
    // Rebased on the page so 2015 = 100 (the published index v2 is already
    // 2015 = 100, so this is a guard); every figure below is growth since 2015,
    // computed, never hand-written.
    const pts = (name: string) => {
      const all = (file.series[name] ? file.series[name].points : []).filter((p) => p.year >= 2015);
      const base = all.length && all[0].year === 2015 ? all[0].value : null;
      return base ? all.map((p) => ({ ...p, value: (p.value / base) * 100 })) : [];
    };
    const dateless = pts('dateless');
    const composite = pts('composite');
    if (dateless.length < 3 || !composite.length) return;
    // Final point is the provisional partial year; last full year precedes it.
    const lastFull = dateless[dateless.length - 2];
    const prior = dateless[dateless.length - 3];
    this.statLastFull = {
      year: lastFull.year,
      value: Math.round(lastFull.value * 10) / 10,
      yoyPct: Math.round(((lastFull.value / prior.value) - 1) * 1000) / 10,
      multiple: Math.round(lastFull.value / 10) / 10,
    };
    const compLastFull = composite[composite.length - 2];
    this.statSinceBase = {
      dateless: Math.round((lastFull.value - 100) * 10) / 10,
      composite: Math.round(compLastFull.value - 100),
    };
  }

  private ingestMedians(asset: FormatMediansAsset): void {
    this.medians = asset;
    const withMedian = asset.records.filter((r) => r.median !== null);
    const sorted = [...withMedian].sort((a, b) => (b.median || 0) - (a.median || 0));
    const solid = sorted.filter((r) => r.confidence !== 'Indicative').map((r) => r.median || 0);
    const hi = Math.log(Math.max(...solid));
    const lo = Math.log(Math.min(...solid));
    this.vbfRows = sorted.map((rec) => ({
      rec,
      barPct: rec.confidence === 'Indicative'
        ? 100
        : Math.max(4, Math.min(100, ((Math.log(rec.median || 1) - lo) / (hi - lo)) * 100)),
    }));
    // The approved table ladder: the six formats of the prototype, value asc.
    const ladder = ['3x3', '3x2', '2x2', '2x1', '1x2', '1x1'];
    this.tableRows = ladder
      .map((t) => withMedian.find((r) => r.classification.token === t))
      .filter((r): r is FormatMedianRecord => !!r);
  }

  /** Display rounding, the prototype convention: £100 steps.

      Indicative formats show the OBSERVED RANGE, not a median. 1×1 is the
      case that matters: eight sales in the window, none of them the marks
      that define the format at the top. A single "≈ £200,000" reads as a
      going rate for a 1×1, which it is not. The range says what the corpus
      actually saw, and the caveat note carries the rest. */
  public money(rec: FormatMedianRecord): string {
    if (rec.confidence === 'Indicative' && rec.windowSampleSize > 1
        && rec.windowLow && rec.windowHigh) {
      return this.round10k(rec.windowLow) + ' to ' + this.round10k(rec.windowHigh);
    }
    const v = rec.median || 0;
    return '£' + (Math.round(v / 100) * 100).toLocaleString('en-GB');
  }

  /** "July 2026" from the asset's own coverage string, so the page never
      claims a currency date the data does not have. */
  public get medianAsOf(): string {
    const cov = this.medians && this.medians.source && this.medians.source.corpusCoverage;
    const end = cov ? cov.split('..').pop()!.trim() : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return '';
    const d = new Date(end + 'T00:00:00Z');
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }

  /** What the figure IS, for the label beside it. A median of one sale is not
      a median, and a range of one sale is not a range. */
  public basisLabel(rec: FormatMedianRecord): string {
    if (rec.confidence !== 'Indicative') return '';
    if (rec.windowSampleSize === 1) return 'One sale in the window';
    return 'Observed range, n = ' + rec.windowSampleSize;
  }

  public basisShort(rec: FormatMedianRecord): string {
    if (rec.confidence !== 'Indicative') return 'median achieved';
    return rec.windowSampleSize === 1 ? 'the only sale in the window' : 'observed range';
  }

  private round10k(v: number): string {
    return '£' + (Math.round(v / 10000) * 10000).toLocaleString('en-GB');
  }

  public exampleFor(rec: FormatMedianRecord): string {
    return 'ABCD'.slice(0, rec.classification.letters) + ' ' +
           '1234'.slice(0, rec.classification.numbers);
  }

  public trackByToken(_i: number, row: { rec: FormatMedianRecord }): string {
    return row.rec.classification.token;
  }

  public trackByRec(_i: number, rec: FormatMedianRecord): string {
    return rec.classification.token;
  }

  public trackBySale(_i: number, s: NotableSale): string {
    return s.plate;
  }

  /** The route base this instance lives on: the unindexed draft during
      review, the public reference after the Phase 8 swap. Selection stays on
      whichever base the visitor arrived at. */
  private get basePath(): string {
    return this.router.url.startsWith('/dateless-draft')
      ? '/dateless-draft'
      : DATELESS_REFERENCE_PATH;
  }

  /** Pill selection: write the format into the URL (no reload), like §4.3. */
  public selectFormat(f: DatelessFormat): void {
    this.router.navigate([this.basePath, f.token]);
  }

  public isSelected(f: DatelessFormat): boolean {
    return !!this.selectedFormat && this.selectedFormat.token === f.token;
  }

  public trackByFormat(_i: number, f: DatelessFormat): string {
    return f.token;
  }

  public trackByGroup(_i: number, g: FormatGroup): number {
    return g.letters;
  }

  public trackByChapter(_i: number, c: Chapter): string {
    return c.id;
  }

  public trackByMilestone(i: number, _m: TimelineMilestone): number {
    return i;
  }

  public jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (target) target.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* Shell measurement, the register-page pattern (names/number-ones). */
  private measureShellBound = (): void => this.measureShellSettled();

  private measureShellSettled(): void {
    requestAnimationFrame(() => this.measureShell());
    setTimeout(() => this.measureShell(), 250);
  }

  private measureShell(): void {
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
  }
}
